import { supabase } from '@/utils/supabase';
import type { PickupEligibleProduct, PricingWaterfall } from './types';
import type { PickupPricingRunSource } from './pickupRunHistory';

export const PICKUP_PRICING_RUN_PAGE_SIZE = 5;

const PICKUP_PRICING_RUN_COLUMNS = [
  'id',
  'analysis_id',
  'ob_loan_id',
  'created_at',
  'locked_price',
  'locked_investor_price',
  'locked_investor',
  'loan_amount',
  'basis_lock_period',
  'current_investor_price',
  'eligible_products',
  'pricing_waterfall'
].join(', ');

interface PickupPricingRunResultRow {
  id: string;
  analysis_id: string;
  ob_loan_id: number;
  created_at: string;
  locked_price: number;
  locked_investor_price: number | null;
  locked_investor: string | null;
  loan_amount: number | null;
  basis_lock_period: number | null;
  current_investor_price: number | null;
  eligible_products: PickupEligibleProduct[] | null;
  pricing_waterfall: PricingWaterfall | null;
}

export interface PickupPricingRunHistoryPage {
  sources: PickupPricingRunSource[];
  hasMore: boolean;
  nextOffset: number | null;
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function pickupPricingRunSourceFromResult(
  row: PickupPricingRunResultRow
): PickupPricingRunSource | null {
  const lockedPrice = finiteNumber(row.locked_price);
  if (!row.analysis_id || !row.created_at || lockedPrice === null) {
    return null;
  }
  return {
    runId: row.analysis_id,
    analyzedAt: row.created_at,
    lockedPrice,
    lockedInvestorPrice: finiteNumber(row.locked_investor_price),
    currentInvestorPrice: finiteNumber(row.current_investor_price),
    currentInvestor: row.locked_investor,
    loanAmount: finiteNumber(row.loan_amount),
    basisLockPeriod: finiteNumber(row.basis_lock_period),
    products: Array.isArray(row.eligible_products) ? row.eligible_products : [],
    pricingWaterfall: row.pricing_waterfall ?? null
  };
}

export async function fetchPickupPricingRunHistoryPage(input: {
  obLoanId: number;
  offset?: number;
  pageSize?: number;
}): Promise<PickupPricingRunHistoryPage> {
  const offset = Math.max(0, Math.trunc(input.offset ?? 0));
  const pageSize = Math.max(1, Math.trunc(input.pageSize ?? PICKUP_PRICING_RUN_PAGE_SIZE));
  const { data, error } = await supabase
    .from('ob_pickup_results')
    .select(PICKUP_PRICING_RUN_COLUMNS)
    .eq('ob_loan_id', input.obLoanId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(offset, offset + pageSize);

  if (error) {
    throw new Error(`Could not load pricing run history: ${error.message}`);
  }

  const page = ((data || []) as unknown as PickupPricingRunResultRow[]);
  const hasMore = page.length > pageSize;
  const sources = page
    .slice(0, pageSize)
    .map(pickupPricingRunSourceFromResult)
    .filter((source): source is PickupPricingRunSource => source !== null);

  return {
    sources,
    hasMore,
    nextOffset: hasMore ? offset + pageSize : null
  };
}
